import type { NavigateFunction } from 'react-router-dom';

/**
 * Global navigation service for handling navigation outside of React components
 * This is particularly useful for API error handling where we need to redirect
 * to login page when authentication fails.
 */
class NavigationService {
  private navigate: NavigateFunction | null = null;

  /**
   * Register the React Router navigate function
   * This should be called from the App component on mount
   */
  setNavigate(navigate: NavigateFunction): void {
    this.navigate = navigate;
  }

  /**
   * Navigate to a specific path
   * @param to - The path to navigate to
   * @param options - Navigation options (replace, state, etc.)
   */
  navigateTo(to: string, options?: { replace?: boolean; state?: unknown }): void {
    if (!this.navigate) {
      console.warn('Navigation service not initialized. Cannot navigate to:', to);
      return;
    }
    
    this.navigate(to, options);
  }

  /**
   * Navigate to login page with current location preserved
   * @param currentPath - The current path to preserve for post-login redirect
   */
  redirectToLogin(currentPath?: string): void {
    const from = currentPath || window.location.pathname;
    this.navigateTo('/login', {
      replace: true,
      state: { from: { pathname: from } }
    });
  }

  /**
   * Check if navigation service is ready
   */
  isReady(): boolean {
    return this.navigate !== null;
  }
}

// Export singleton instance
export const navigationService = new NavigationService(); 